import React, { useEffect, useState, useRef } from 'react';
import { Box, Slide } from '@mui/material';
import { useSidePanelContext } from '../../contexts/SidePanelContext';

interface CascadingSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  children: React.ReactNode;
  panelId: string;
  title?: string;
}

const PANEL_WIDTH_PERCENTAGE = 20; // Each panel takes 30% of viewport width
const MAX_VISIBLE_PANELS = 5; // Maximum panels visible side by side before overlapping starts

const CascadingSidePanel: React.FC<CascadingSidePanelProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  children,
  panelId,
  title,
}) => {
  const { registerPanel, unregisterPanel, getPanelLevel, getTotalPanels, closeAll } = useSidePanelContext();
  const [panelLevel, setPanelLevel] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const hasRegistered = useRef(false);

  useEffect(() => {
    if (isOpen && !hasRegistered.current) {
      const level = registerPanel(panelId, title);
      setPanelLevel(level);
      hasRegistered.current = true;
    } else if (!isOpen && hasRegistered.current) {
      // Unregister immediately when panel closes, not just on unmount
      unregisterPanel(panelId);
      hasRegistered.current = false;
    }
    
    return () => {
      if (hasRegistered.current) {
        unregisterPanel(panelId);
        hasRegistered.current = false;
      }
    };
  }, [isOpen, panelId, title, registerPanel, unregisterPanel]);

  useEffect(() => {
    if (isOpen) {
      const level = getPanelLevel(panelId);
      setPanelLevel(level);
    }
  }, [isOpen, panelId, getPanelLevel]);

  // Handle window resize to update panel width
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPanels = getTotalPanels();
  
  // Calculate panel width as 30% of viewport width
  const panelWidth = Math.floor(windowWidth * (PANEL_WIDTH_PERCENTAGE / 100));
  
  // Calculate position from right
  // Rightmost panel (highest level) is at right: 0
  // Each previous panel is shifted left by panel width
  // When there are more than MAX_VISIBLE_PANELS, overlap the leftmost panels
  let rightPosition: number;
  
  if (totalPanels <= MAX_VISIBLE_PANELS) {
    // All panels fit side by side
    rightPosition = (totalPanels - 1 - panelLevel) * panelWidth;
  } else {
    // More panels than can fit - overlap the leftmost ones
    const visiblePanelsFromRight = totalPanels - panelLevel;
    
    if (visiblePanelsFromRight <= MAX_VISIBLE_PANELS) {
      // This panel is within the visible range from the right
      rightPosition = (visiblePanelsFromRight - 1) * panelWidth;
    } else {
      // This panel is beyond the visible range - overlap it at the leftmost position
      // Position it slightly behind the leftmost visible panel for a stacked effect
      const overlapOffset = (visiblePanelsFromRight - MAX_VISIBLE_PANELS) * 20; // 20px offset per hidden panel
      rightPosition = (MAX_VISIBLE_PANELS - 1) * panelWidth + overlapOffset;
    }
  }
  
  // Calculate z-index - higher level = higher z-index
  const zIndex = 1300 + panelLevel;
  
  // Determine if this panel should be fully visible or semi-transparent
  const isFullyVisible = panelLevel >= totalPanels - MAX_VISIBLE_PANELS || totalPanels <= MAX_VISIBLE_PANELS;
  
  const handleBackdropClick = () => {
    if (onCloseAll) {
      onCloseAll();
      closeAll();
    } else {
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop - only show for the first (leftmost) panel when panels are actually open */}
      {isOpen && panelLevel === 0 && totalPanels > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1299,
          }}
          onClick={handleBackdropClick}
        />
      )}

      {/* Side Panel */}
      <Slide 
        direction="left" 
        in={isOpen} 
        mountOnEnter 
        unmountOnExit
        timeout={300}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: `${rightPosition}px`,
            height: '100vh',
            width: `${panelWidth}px`,
            bgcolor: 'background.paper',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
            zIndex: zIndex,
            display: 'flex',
            flexDirection: 'column',
            opacity: isFullyVisible ? 1 : 0.6,
            transition: 'right 0.3s ease-in-out, width 0.3s ease-in-out, opacity 0.2s ease-in-out',
            // Add a border to distinguish panels
            borderLeft: panelLevel > 0 ? '2px solid' : 'none',
            borderColor: 'divider',
            // Add pointer events control - allow interaction with visible panels
            pointerEvents: isFullyVisible ? 'auto' : 'none',
            // Add visual feedback for overlapped panels
            filter: isFullyVisible ? 'none' : 'grayscale(0.3)',
            transform: isFullyVisible ? 'scale(1)' : 'scale(0.98)',
          }}
        >
          {children}
          
          {/* Visual indicator for hidden panels */}
          {!isFullyVisible && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                pointerEvents: 'none',
                zIndex: 10000,
              }}
            />
          )}
        </Box>
      </Slide>
    </>
  );
};

export default CascadingSidePanel;

